import httpClient from '../../../api/httpClient';

export type DiscountType = 'PERCENTAGE' | 'FIXED';

export type OrderItem = {
  id: string;
  itemCode: string;
  itemName: string;
  description: string;
  itemType: 'INVENTORY' | 'SERVICE';
  itemGroup: string;
  unitOfMeasure: string;
  costPrice: number;
  sellingPrice: number;
  originalPrice: number; // Original price before any discounts
  discount: number; // Discount amount (either fixed or percentage)
  discountType: DiscountType; // 'PERCENTAGE' or 'FIXED'
  taxPercentage: number;
  reorderLevel?: number;
  isActive: boolean;
  quantity: number;
  createdAt?: string;
  updatedAt?: string;
  createdBy?: string;
  updatedBy?: string;
};

interface ApiItem extends Omit<OrderItem, 'itemType'> {
  type: 'SERVICE' | 'PRODUCT';
}

const mapApiItemToOrderItem = (item: any): OrderItem => ({
  id: item.id,
  itemCode: item.itemCode,
  itemName: item.itemName,
  description: item.description || 'No description',
  itemType: item.itemType,
  itemGroup: item.itemGroup,
  unitOfMeasure: item.unitOfMeasure,
  costPrice: item.costPrice || 0,
  sellingPrice: item.sellingPrice || 0,
  originalPrice: item.sellingPrice || 0, // Store original price
  discount: 0, // Default discount
  discountType: 'FIXED', // Default to fixed discount
  taxPercentage: item.taxPercentage || 0,
  reorderLevel: item.reorderLevel,
  isActive: item.isActive,
  quantity: 1, // Default quantity when adding to order
  createdAt: item.createdAt,
  updatedAt: item.updatedAt,
  createdBy: item.createdBy,
  updatedBy: item.updatedBy,
});

export const orderItemsApi = {
  getOrderItems: async (search: string = ''): Promise<OrderItem[]> => {
    try {
      const response = await httpClient.get<{
        data: any[];
        total: number;
        page: number;
        limit: number;
        totalPages: number;
      }>('/items', {
        params: {
          isActive: true,
          search: search || undefined,
        },
      });
      
      if (!response.data?.data) {
        console.warn('No data in response or invalid response format');
        return [];
      }
      
      return response.data.data.map(mapApiItemToOrderItem);
    } catch (error) {
      console.error('Failed to fetch order items:', error);
      return [];
    }
  },

  getServiceItems: async (search: string = ''): Promise<OrderItem[]> => {
    try {
      console.log('Fetching service items with search:', search);
      const response = await httpClient.get<{
        data: any[];
        total: number;
        page: number;
        limit: number;
        totalPages: number;
      }>('/items', {
        params: {
          itemType: 'SERVICE',
          isActive: true,
          search: search || undefined,
        },
      });
      
      if (!response.data || !response.data.data) {
        console.warn('No data in response or invalid response format');
        return [];
      }
      
      console.log('Service items response:', response.data);
      console.log('Raw items data:', response.data.data);
      
      const mappedItems = response.data.data.map(mapApiItemToOrderItem);
      console.log('Mapped service items:', mappedItems);
      return mappedItems;
    } catch (err: unknown) {
      const error = err as {
        response?: { data?: any; status?: number; headers?: any };
        request?: any;
        message: string;
      };
      
      console.error('Failed to fetch service items:', error);
      
      if (error.response) {
        console.error('Error response data:', error.response.data);
        console.error('Error status:', error.response.status);
        console.error('Error headers:', error.response.headers);
      } else if (error.request) {
        console.error('No response received. Request details:', {
          method: error.request.method,
          url: error.request.responseURL,
          status: error.request.status,
          statusText: error.request.statusText,
        });
      } else {
        console.error('Error message:', error.message);
      }
      
      // Return empty array instead of throwing to prevent UI from breaking
      return [];
    }
  },

  getInventoryItems: async (search: string = ''): Promise<OrderItem[]> => {
    try {
      const response = await httpClient.get<{
        data: any[];
        total: number;
        page: number;
        limit: number;
        totalPages: number;
      }>('/items', {
        params: {
          itemType: 'INVENTORY',
          isActive: true,
          search: search || undefined,
        },
      });
      
      if (!response.data?.data) {
        console.warn('No data in response or invalid response format');
        return [];
      }
      
      return response.data.data.map(mapApiItemToOrderItem);
    } catch (error) {
      console.error('Failed to fetch inventory items:', error);
      return [];
    }
  },
};
